
import { defineComponent, PropType } from 'vue'
import { TRIAL_PERIOD_DAYS, SALES_URL } from '~/utils/billing'

interface ChecklistItem {
  icon: string
  label: string
  action: 'members' | 'support'
}

export default defineComponent({
  name: 'TrialCard',
  props: {
    daysRemaining: {
      type: Number,
      required: true
    },
    totalTrialDays: {
      type: Number,
      default: TRIAL_PERIOD_DAYS
    },
    checklistItems: {
      type: Array as PropType<ChecklistItem[]>,
      default: () => [
        { icon: 'users', label: 'Invite colleagues', action: 'members' as const },
        { icon: 'headset', label: 'Schedule walkthrough', action: 'support' as const }
      ]
    },
    canInviteMembers: {
      type: Boolean,
      default: true
    }
  },
  computed: {
    filteredChecklistItems(): ChecklistItem[] {
      if (!this.canInviteMembers) {
        return this.checklistItems.filter((item) => item.action !== 'members')
      }
      return this.checklistItems
    },
    isExpired(): boolean {
      return this.daysRemaining <= 0
    },
    isEndingImminently(): boolean {
      return this.daysRemaining <= 1
    },
    isEndingSoon(): boolean {
      return !this.isEndingImminently && this.daysRemaining >= 2 && this.daysRemaining <= 7
    },
    badgeText(): string {
      if (this.isExpired) {
        return 'Ended'
      }
      return `${this.daysRemaining} ${this.daysRemaining === 1 ? 'day' : 'days'} left`
    },
    badgeClasses(): string {
      if (this.isEndingImminently) {
        return 'bg-cherry-500/20'
      }
      if (this.isEndingSoon) {
        return 'bg-honey-500/20'
      }
      return 'bg-vanilla-400/10'
    },
    badgeTextClasses(): string {
      if (this.isEndingImminently) {
        return 'text-cherry-500'
      }
      if (this.isEndingSoon) {
        return 'text-honey-500'
      }
      return 'text-vanilla-400'
    },
    badgeAriaLabel(): string {
      if (this.isExpired) {
        return 'Trial has ended'
      }
      if (this.daysRemaining <= 1) {
        return 'Trial ending soon - 1 day left'
      }
      if (this.daysRemaining <= 7) {
        return `Trial ending soon - ${this.daysRemaining} days left`
      }
      return `Trial active - ${this.daysRemaining} days remaining`
    },
    remainingPercentage(): number {
      return Math.round((this.daysRemaining / this.totalTrialDays) * 100)
    },
    progressBarClasses(): string {
      if (this.remainingPercentage > 50) {
        return 'bg-robin-500'
      }
      if (this.remainingPercentage >= 25) {
        return 'bg-honey-500'
      }
      return 'bg-cherry-500'
    }
  },
  methods: {
    getItemAriaLabel(item: ChecklistItem): string {
      switch (item.action) {
        case 'members':
          return `${item.label} - navigate to team members page`
        case 'support':
          return `${item.label} - opens in new window`
        default:
          return item.label
      }
    },
    handleItemClick(item: ChecklistItem) {
      const { provider, owner } = this.$route.params

      if (!provider || !owner) {
        this.$sentry.captureMessage('Trial card: Missing route params', {
          level: 'warning',
          extra: { action: item.action }
        })
        return
      }

      switch (item.action) {
        case 'members':
          this.$router.push(
            this.$generateRoute(['members'], false, true, provider as string, owner as string)
          )
          break
        case 'support': {
          // Open DeepSource contact sales page in new tab
          const newWindow = window.open(SALES_URL, '_blank', 'noopener,noreferrer')

          if (!newWindow) {
            this.$toast.info(
              'Popup blocked by your browser. Please allow popups for this site and try again.'
            )
          }
          break
        }
        default:
          break
      }
    },
    navigateToBilling() {
      const { provider, owner } = this.$route.params

      if (!provider || !owner) {
        this.$sentry.captureMessage('Trial card: Missing route params for billing navigation', {
          level: 'warning'
        })
        return
      }

      this.$router.push(
        this.$generateRoute(
          ['settings', 'billing'],
          false,
          true,
          provider as string,
          owner as string
        )
      )
    }
  }
})
